import { Paper, Stack } from "@mui/material";
import { isInteger } from "lodash";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { parseJson } from "utils/dataManipulation";

import { useGetProjectInfo } from "../../api/hooks/allHooks";
import { WizardContext } from "../../context/Wizard";
import { eventNames, withGAEventTracking } from "../../functions/googleAnalytics";
import { calculateStrandingPoint } from "../../functions/Modernization";
import { theme } from "../../styles/theme";
import { formatNumberBasedOnUser } from "../../utils/namesManipulation";
import { getCSSVar } from "../../utils/styling";
import { DynamicEchartsChart } from "../chart/DynamicChartMemoized";
import { legendsConfigAtBottom } from "../chart/DynamicChartMemoized";
import { DropdownSingleSelect } from "../DropdownSingleSelect";
import { RadioButton } from "../RadioButton";

const noopCrremChange = (_crremData) => {};

export const StrandingChartWithEchart = ({
  chartHeight = "35rem",
  className,
  hasModernization = true,
  onCrremChangeUseCallback = noopCrremChange,
  projectId,
  title,
}) => {
  const { data: projectInfo } = useGetProjectInfo(projectId);

  const { crremData, crremScenario, data, quantity, setCrremScenario, setData, setQuantity, yearRange } =
    useContext(WizardContext);

  const crremScenarioList = useMemo(() => {
    return parseJson(projectInfo?.[0]?.crrem_scenario_list) ?? [];
  }, [projectInfo]);

  let crremScenariosOptions = crremScenarioList?.map((option) => ({ option }));

  const unit = quantity === "emission" ? "CO2/m²" : "kWh/m²";

  useEffect(() => {
    setCrremScenario((prev) => prev ?? crremScenarioList?.[0]);
  }, [projectInfo, setCrremScenario, crremScenarioList]);

  const onCrremChangeRef = useRef(onCrremChangeUseCallback);
  onCrremChangeRef.current = onCrremChangeUseCallback;

  useEffect(() => {
    const nextData = crremData || [];
    setData((prev) => {
      if (prev === nextData) return prev;
      if (prev.length === 0 && nextData.length === 0) return prev;
      return nextData;
    });
    onCrremChangeRef.current?.(crremData);
  }, [crremData, setData]);

  const [strandingPoint, setStrandingPoint] = useState([]);
  const [strandingPointsModernized, setStrandingPointsModernized] = useState([]);

  useEffect(() => {
    const enhancedData = data.map((e) => {
      if (e.crrem !== undefined) return e;
      const year = e.year;
      const yearData = data.find((e) => e.year === year && e.crrem !== undefined);
      if (yearData) return { ...e, crrem: yearData.crrem };
      return e;
    });
    setStrandingPointsModernized(calculateStrandingPoint(enhancedData, "modernized"));
    setStrandingPoint(calculateStrandingPoint(data, "current"));
  }, [data]);

  // Just for dropdown initial value to display!
  if (!projectInfo) return;

  const dataForChart = data?.filter((yearData) => yearData.year <= yearRange[1] && yearData.year >= yearRange[0]);
  const option = {
    animation: true,
    dataZoom: [
      {
        right: 0,
        type: "slider",
        width: 16,
        yAxisIndex: 0,
      },
    ],
    grid: {
      ...legendsConfigAtBottom.grid,
      left: 30,
      right: 30,
      top: 35,
    },
    legend: {
      bottom: 0,
      left: 10,
      orient: "horizontal",
      right: 40,
      textStyle: {
        fontFamily: "var(--font-primary)",
        fontSize: "1.3rem",
        fontWeight: 600,
      },
    },

    series: [
      ...(quantity === "emission"
        ? [
            {
              color: getCSSVar("--color-purpose-modernization-charts-baseline-emission"),
              connectNulls: true,
              data: dataForChart.map((d) => [d.year, d.baseline]),
              lineStyle: {
                color: getCSSVar("--color-purpose-modernization-charts-baseline-emission"),
                type: "dashed",
                width: 1,
              },
              name: "Baseline Emission",
              symbol: "none",
              type: "line",
            },
          ]
        : []),

      ...(hasModernization
        ? [
            {
              color: getCSSVar("--color-purpose-modernization-charts-modernized"),
              data: dataForChart.map((d) => [d.year, d.modernized]),
              lineStyle: { color: getCSSVar("--color-purpose-modernization-charts-modernized"), width: 2 },
              name: "Modernized",
              symbol: "none",
              type: "line",
            },
            {
              color: getCSSVar("--color-purpose-modernization-charts-retrofit"),
              data: dataForChart.map((d) => [d.year, d.retrofit]),
              itemStyle: { color: getCSSVar("--color-purpose-modernization-charts-retrofit") },
              lineStyle: {
                color: getCSSVar("--color-purpose-modernization-charts-retrofit"),
                type: "dashed",
                width: 2,
              },
              name: "Retrofit",
              symbol: "circle",
              symbolSize: 6,
              type: "line",
            },
            ...(strandingPointsModernized?.map((sP, i) => ({
              data: [[sP[0], sP[1]]],
              itemStyle: {
                borderColor: getCSSVar("--color-purpose-modernization-charts-stranding-event"),
                borderWidth: 3,
                color: "none",
                shadowBlur: 4,
                shadowColor: getCSSVar("--color-purpose-modernization-charts-stranding-event-shadow"),
              },
              name: `Stranding Event ${i}`,
              symbolSize: 16,
              type: "scatter",
            })) || []),
            ...(strandingPoint?.length > 0
              ? [
                  {
                    data: [[strandingPoint[0][0], strandingPoint[0][1]]],
                    itemStyle: {
                      borderColor: getCSSVar("--color-purpose-modernization-charts-stranding-event"),
                      borderWidth: 3,
                      color: "none",
                      shadowBlur: 4,
                      shadowColor: getCSSVar("--color-purpose-modernization-charts-stranding-event-shadow"),
                    },
                    name: "Stranding Event",
                    symbolSize: 16,
                    type: "scatter",
                  },
                ]
              : []),
          ]
        : []),

      {
        color: getCSSVar("--color-purpose-modernization-charts-current-performance"),
        connectNulls: true,
        data: dataForChart.map((d) => [d.year, d.current]),
        lineStyle: { color: getCSSVar("--color-purpose-modernization-charts-current-performance"), width: 1 },
        name: quantity === "consumption" ? "Asset Energy Intensity" : "Climate And Grid Corrected Asset Performance",
        symbol: "none",
        type: "line",
      },
      {
        areaStyle: { color: getCSSVar("--color-purpose-modernization-charts-decarbonization-target"), opacity: 0.05 },
        color: getCSSVar("--color-purpose-modernization-charts-decarbonization-target"),
        connectNulls: true,
        data: dataForChart.map((d) => [d.year, d.crrem]),
        lineStyle: { color: getCSSVar("--color-purpose-modernization-charts-decarbonization-target"), width: 1 },
        name: quantity === "consumption" ? "Country Specific Energy Target" : "Decarbonization Target",
        symbol: "none",
        type: "line",
      },
    ],
    tooltip: {
      formatter: (params) => {
        const year = params?.[0]?.data?.[0];
        const filtered = params
          .filter((p) => {
            return p.data?.[1] !== null && p.data?.[1] !== undefined && p.seriesName !== "Retrofit";
          })
          .map((p) => `${p.marker} ${p.seriesName}: ${formatNumberBasedOnUser(p?.data?.[1])}`)
          .join("<br/>");
        return `<h1>${isInteger(year) ? year : year?.toFixed(2)}</h1>
        ${filtered} `;
      },
      trigger: "axis",
    },
    xAxis: [
      {
        axisPointer: {
          type: "shadow",
        },
        max: yearRange[1],
        min: yearRange[0],
        name: "Year",
        nameGap: 25,
        nameLocation: "center",
        type: "value",
      },
    ],

    yAxis: [
      {
        name: unit,
        nameGap: 10,
        nameLocation: "end",
        nameRotate: 0,
        nameTextStyle: {},
        type: "value",
      },
    ],
  };

  return (
    <Paper className={`relative  ${className}`}>
      <section className="flex items-start justify-between gap-6">
        <div className="w-full">
          {title && <h2 className="font-ds-headline-lg mb-8">{title}</h2>}
          <Stack alignItems="flex-start" justifyContent="space-between">
            <DropdownSingleSelect
              className="mb-6"
              label="Scenario"
              options={crremScenariosOptions.map((e) => e.option)}
              selectedValue={crremScenario ?? crremScenarioList?.[0]}
              setSelectedValue={(newValue) =>
                withGAEventTracking(eventNames.CRREM_SCENARIO_CHANGE, {}, () => setCrremScenario(newValue))
              }
              width="25rem"
            />
            <Stack alignItems="center" className="mb-9" gap={theme.spacing(6)}>
              <RadioButton
                displayOptions={["CO₂ Emission", "kWh"]}
                options={["emission", "consumption"]}
                row={true}
                setValue={(newValue) =>
                  withGAEventTracking(eventNames.CRREM_QUANTITY_CHANGE, {}, () => setQuantity(newValue))
                }
                value={quantity}
              />
            </Stack>
          </Stack>
          <div className="relative " style={{ height: chartHeight }}>
            <DynamicEchartsChart option={option} />
          </div>
        </div>
      </section>
    </Paper>
  );
};
